import authService from './authService';
import { ApiResponse } from '../types/common';
import { FinancialAccount } from '../types/financial';
import { Bank, CheckingAccountFormData } from '../types/checkingAccount';

/**
 * Serviço do cadastro de Conta Corrente ([Financial].[Account]).
 * Endpoints: /financial/checking-accounts (CRUD) e /financial/banks (seletor).
 */
class CheckingAccountService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private async throwApiError(response: Response, fallback: string): Promise<never> {
    let message = fallback;
    try {
      const body = await response.json();
      message = body?.message || body?.error || fallback;
    } catch {
      /* corpo não-JSON — mantém fallback */
    }
    throw new Error(message);
  }

  /** GET /financial/checking-accounts?companyId= (filtro opcional) */
  async getAll(companyId?: number): Promise<FinancialAccount[]> {
    const apiClient = await this.getApiClient();
    const query = companyId != null ? `?companyId=${companyId}` : '';
    const response = await apiClient.get(`/financial/checking-accounts${query}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar contas correntes');
    const data: ApiResponse<FinancialAccount[]> = await response.json();
    return data.data ?? [];
  }

  /** GET /financial/checking-accounts/:id */
  async getById(id: number): Promise<FinancialAccount> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/checking-accounts/${id}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar conta corrente');
    const data: ApiResponse<FinancialAccount> = await response.json();
    return data.data!;
  }

  /** POST /financial/checking-accounts */
  async create(payload: CheckingAccountFormData): Promise<FinancialAccount> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/financial/checking-accounts', payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao criar conta corrente');
    const data: ApiResponse<FinancialAccount> = await response.json();
    return data.data!;
  }

  /** PUT /financial/checking-accounts/:id */
  async update(id: number, payload: CheckingAccountFormData): Promise<FinancialAccount> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/financial/checking-accounts/${id}`, payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao atualizar conta corrente');
    const data: ApiResponse<FinancialAccount> = await response.json();
    return data.data!;
  }

  /** DELETE /financial/checking-accounts/:id */
  async delete(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/financial/checking-accounts/${id}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao excluir conta corrente');
  }

  /** GET /financial/banks */
  async listBanks(): Promise<Bank[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/financial/banks');
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar bancos');
    const data: ApiResponse<Bank[]> = await response.json();
    return data.data ?? [];
  }
}

export const checkingAccountService = new CheckingAccountService();
export default checkingAccountService;
